import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Stars, ChevronDown, ChevronRight, Globe2, X } from 'react-bootstrap-icons';
import './MarkdownRenderer.css';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  isStreaming?: boolean;
  onSuggestedStepClick?: (text: string) => void;
  onModifyArtifactSubmit?: (selectedItems: string[]) => void;
  hideModifyArtifact?: boolean;
}

// Context to track if we're inside a suggested-next-steps section
const SuggestedNextStepsContext = React.createContext(false);

// Context to pass the click handler for suggested next steps
const SuggestedStepClickContext = React.createContext<((text: string) => void) | undefined>(undefined);

// Context for Modify Artifact section
const ModifyArtifactContext = React.createContext<{
  checkedItems: Set<string>;
  toggleItem: (text: string) => void;
} | null>(null);

// Context to track if we're inside a sources section
const SourcesContext = React.createContext(false);

// Custom list item component that can use hooks
const ListItem: React.FC<{ children: any }> = ({ children }) => {
  const isSuggestedNextSteps = React.useContext(SuggestedNextStepsContext);
  const onStepClick = React.useContext(SuggestedStepClickContext);
  const modifyArtifactContext = React.useContext(ModifyArtifactContext);
  const isSources = React.useContext(SourcesContext);

  // Extract text content from nested children
  const extractTextContent = (node: any): string => {
    if (typeof node === 'string') return node;
    if (Array.isArray(node)) {
      return node.map(extractTextContent).join('');
    }
    if (node?.props?.children) {
      return extractTextContent(node.props.children);
    }
    return '';
  };

  const textContent = extractTextContent(children).trim();

  // Sources list item - don't render anything, handled by wrapper
  if (isSources) {
    return null;
  }

  // Modify Artifact list item
  if (modifyArtifactContext) {
    const isChecked = modifyArtifactContext.checkedItems.has(textContent);

    return (
      <li
        className={`markdown-li modify-artifact-item ${isChecked ? 'checked' : ''}`}
        onClick={() => modifyArtifactContext.toggleItem(textContent)}
      >{children}</li>
    );
  }

  // Suggested Next Steps list item
  if (isSuggestedNextSteps) {
    const handleClick = () => {
      if (onStepClick && textContent) {
        onStepClick(textContent);
      }
    };

    return (
      <li
        className="markdown-li suggested-next-steps-item"
        onClick={handleClick}
        style={{ cursor: onStepClick ? 'pointer' : 'default' }}
      >
        <Stars className="suggested-next-steps-icon" />
        <p className="suggested-next-steps-text">{children}</p>
      </li>
    );
  }

  return <li className="markdown-li">{children}</li>;
};

// Resources Panel component
const ResourcesPanel: React.FC<{
  sources: Array<{ title: string; url: string }>;
  onClose: () => void;
}> = ({ sources, onClose }) => {
  // Helper function to clean URL for display
  const cleanUrlForDisplay = (url: string): string => {
    return url
      .replace(/^https?:\/\/www\./, '')
      .replace(/^https?:\/\//, '');
  };

  const panelContent = (
    <>
      <div className="resources-panel-overlay" onClick={onClose} />
      <div className="resources-panel">
        <div className="resources-panel-header">
          <h2 className="resources-panel-title">Resources</h2>
          <button className="resources-panel-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="resources-panel-content">
          {sources.map((source, index) => (
            <a
              key={index}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="resource-item"
            >
              <div className="resource-item-url">
                <Globe2 className="resource-item-icon" />
                <span className="resource-item-url-text">{cleanUrlForDisplay(source.url)}</span>
              </div>
              <div className="resource-item-title">{source.title}</div>
            </a>
          ))}
        </div>
      </div>
    </>
  );

  // Use portal to render at body level, above all other content
  return ReactDOM.createPortal(panelContent, document.body);
};

// Sources Wrapper component
const SourcesWrapper: React.FC<{ children: any; sourcesData: Array<{ title: string; url: string }> }> = ({ children, sourcesData }) => {
  const [showPanel, setShowPanel] = useState(false);

  // Helper function to clean URL for display
  const cleanUrlForDisplay = (url: string): string => {
    return url
      .replace(/^https?:\/\/www\./, '')
      .replace(/^https?:\/\//, '');
  };

  const hasMoreThanFour = sourcesData.length > 4;
  const displayedSources = hasMoreThanFour ? sourcesData.slice(0, 4) : sourcesData;

  return (
    <SourcesContext.Provider value={true}>
      <div className="sources-wrapper">
        {displayedSources.map((source, index) => (
          <a
            key={index}
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="source-box"
          >
            <div className="source-url">
              <Globe2 className="source-icon" />
              <span className="source-url-text">{cleanUrlForDisplay(source.url)}</span>
            </div>
            <div className="source-title">{source.title}</div>
          </a>
        ))}
        {hasMoreThanFour && (
          <div
            className="source-box source-box-view-all"
            onClick={() => setShowPanel(true)}
          >
            <div className="source-url">
              <Globe2 className="source-icon" />
              <span className="source-url-text source-view-all-count">{sourcesData.length} RESOURCES</span>
            </div>
            <div className="source-title source-view-all-text">VIEW ALL</div>
          </div>
        )}
      </div>
      {showPanel && (
        <ResourcesPanel sources={sourcesData} onClose={() => setShowPanel(false)} />
      )}
    </SourcesContext.Provider>
  );
};

// Modify Artifact Wrapper component with button
const ModifyArtifactWrapper: React.FC<{
  children: any;
  checkedItems: Set<string>;
  toggleItem: (text: string) => void;
  onSubmit: () => void;
}> = ({ children, checkedItems, toggleItem, onSubmit }) => {
  return (
    <ModifyArtifactContext.Provider value={{ checkedItems, toggleItem }}>
      <div className="modify-artifact-wrapper">
        {children}
        <button
          className="modify-artifact-update-btn"
          disabled={checkedItems.size === 0}
          onClick={onSubmit}
        >
          <Stars className="modify-artifact-update-icon" size={12} />
          UPDATE CHANGES
        </button>
      </div>
    </ModifyArtifactContext.Provider>
  );
};

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '', isStreaming = false, onSuggestedStepClick, onModifyArtifactSubmit, hideModifyArtifact = false }) => {
  const svgCodeBlockRef = useRef<HTMLDivElement>(null);
  const previousContentRef = useRef<string>('');
  const [isSvgCodeCollapsed, setIsSvgCodeCollapsed] = useState(false);
  const [checkedModifyItems, setCheckedModifyItems] = useState<Set<string>>(new Set());
  const [sourcesData, setSourcesData] = useState<Map<number, Array<{ title: string; url: string }>>>(new Map());

  // Memoize the toggle handler to prevent issues during rapid re-renders (streaming)
  const toggleSvgCodeCollapsed = useCallback(() => {
    setIsSvgCodeCollapsed(prev => !prev);
  }, []);

  const toggleModifyItem = (text: string) => {
    setCheckedModifyItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(text)) {
        newSet.delete(text);
      } else {
        newSet.add(text);
      }
      return newSet;
    });
  };

  const handleUpdateChanges = () => {
    if (onModifyArtifactSubmit && checkedModifyItems.size > 0) {
      const selectedItems = Array.from(checkedModifyItems);
      onModifyArtifactSubmit(selectedItems);
      setCheckedModifyItems(new Set()); // Clear selections after submitting
    }
  };

  useEffect(() => {
    // Auto-scroll SVG code block when content is being streamed
    if (isStreaming && svgCodeBlockRef.current && content !== previousContentRef.current) {
      svgCodeBlockRef.current.scrollTo({
        top: svgCodeBlockRef.current.scrollHeight,
        behavior: 'smooth'
      });
      previousContentRef.current = content;
    }
  }, [content, isStreaming]);

  // Parse sources from content when it changes
  useEffect(() => {
    const newSourcesData = new Map<number, Array<{ title: string; url: string }>>();
    let sourceSectionIndex = 0;

    // Match "Sources" header followed by ordered or unordered list
    // Supports multiple header formats:
    // - Markdown headers: "# Sources", "## Sources", "### Sources"
    // - Bold text: "**Sources:**", "**Sources**"
    // - Plain text: "Sources:", "Sources"
    // List formats: "1. item", "- item", "* item"
    const sourcesRegex = /((?:#{1,3}\s+Sources|\*\*Sources:?\*\*|Sources:?)\s*\n+)((?:(?:\d+\.|-|\*)\s+.+\n?)+)/gi;
    let match;

    while ((match = sourcesRegex.exec(content)) !== null) {
      const list = match[2];
      const currentIndex = sourceSectionIndex++;
      const sources: Array<{ title: string; url: string }> = [];

      // Parse each line in the list
      const lines = list.trim().split('\n');
      lines.forEach((line: string) => {
        // Match pattern: "1. content", "- content", or "* content"
        const itemMatch = line.match(/^(?:\d+\.|-|\*)\s+(.+)$/);
        if (itemMatch) {
          const contentStr = itemMatch[1].trim();

          // Try markdown link format: [title](url) - can be anywhere in the line
          const markdownLinkMatch = contentStr.match(/\[([^\]]+)\]\(([^)]+)\)/);
          if (markdownLinkMatch) {
            sources.push({ title: markdownLinkMatch[1].trim(), url: markdownLinkMatch[2].trim() });
            return;
          }

          // Try title:url format (find last colon before http to handle URLs with colons)
          // Look for patterns like "title:https://..." or "title:http://..."
          const urlMatch = contentStr.match(/^(.+?):\s*(https?:\/\/.+)$/i);
          if (urlMatch) {
            sources.push({ title: urlMatch[1].trim(), url: urlMatch[2].trim() });
            return;
          }

          // Try to find a URL anywhere in the content
          const standaloneUrlMatch = contentStr.match(/(https?:\/\/[^\s]+)/i);
          if (standaloneUrlMatch) {
            // Use the text before the URL as title, or the URL itself if no text
            const urlIndex = contentStr.indexOf(standaloneUrlMatch[1]);
            const titlePart = contentStr.substring(0, urlIndex).trim().replace(/[:\-–]\s*$/, '').trim();
            sources.push({
              title: titlePart || standaloneUrlMatch[1],
              url: standaloneUrlMatch[1]
            });
            return;
          }

          // Fallback: If no URL found, skip this item (sources need URLs for cards)
          // This prevents showing broken cards with no links
        }
      });

      newSourcesData.set(currentIndex, sources);
    }

    setSourcesData(newSourcesData);
  }, [content]);

  // Remove only INLINE SVG tags (not in code blocks) since they're displayed in the canvas
  const cleanedContent = content.replace(/<svg[\s\S]*?<\/svg>/gi, (match) => {
    // Check if SVG is inside a code block (don't remove those)
    const beforeSvg = content.substring(0, content.indexOf(match));
    const codeBlockCount = (beforeSvg.match(/```/g) || []).length;

    // If odd number of ``` before SVG, it's inside a code block - keep it
    if (codeBlockCount % 2 === 1) {
      return match;
    }

    // Otherwise, it's inline - replace with placeholder since it's shown in canvas
    return '\n\n*[SVG displayed in canvas →]*\n\n';
  });

  // Wrap "Modify Artifact" sections with a special marker
  // This handles both unordered lists (-, *, +) and ordered lists (1., 2., etc.)
  // Keep the markdown header intact and let ReactMarkdown parse it naturally
  // Only process if not already wrapped to prevent nested wrappers
  let processedContent = cleanedContent;
  if (!cleanedContent.includes('modify-artifact-section')) {
    processedContent = cleanedContent.replace(
      /(#{1,3}\s*.*Modify\s+Artifact.*\s*\n+(?:(?:[-*+]|\d+\.)\s+.+(?:\n(?!\n|#{1,3}).*)*\n?)+)/gi,
      (match) => {
        // Wrap the entire matched section (header + list) in a div
        // ReactMarkdown will parse the markdown header naturally
        return `<div class="modify-artifact-section">\n\n${match.trim()}\n\n</div>\n\n`;
      }
    );
  }

  // Wrap "Suggested Next Steps" sections with a special marker
  // This handles both unordered lists (-, *, +) and ordered lists (1., 2., etc.)
  // Keep the markdown header intact and let ReactMarkdown parse it naturally
  // Only process if not already wrapped to prevent nested wrappers
  if (!processedContent.includes('suggested-next-steps-section')) {
    processedContent = processedContent.replace(
      /(#{1,3}\s*.*Suggested\s+Next\s+Steps.*\s*\n+(?:(?:[-*+]|\d+\.)\s+.+(?:\n(?!\n|#{1,3}).*)*\n?)+)/gi,
      (match) => {
        // Wrap the entire matched section (header + list) in a div
        // ReactMarkdown will parse the markdown header naturally
        return `<div class="suggested-next-steps-section">\n\n${match.trim()}\n\n</div>\n\n`;
      }
    );
  }

  // Wrap "Sources" sections with a special marker
  // Match "Sources" header followed by ordered or unordered list
  // Supports: markdown headers, bold text, or plain "Sources:" format
  let sourceSectionIndex = 0;

  processedContent = processedContent.replace(
    /((?:#{1,3}\s+Sources|\*\*Sources:?\*\*|Sources:?)\s*\n+)((?:(?:\d+\.|-|\*)\s+.+\n?)+)/gi,
    (match, header, list) => {
      // Only replace if the list contains URLs (markdown links or http URLs)
      // This prevents replacing valid source content with empty cards
      const hasUrls = /\[([^\]]+)\]\(([^)]+)\)|https?:\/\//.test(list);
      if (!hasUrls) {
        // No URLs found, keep original content as-is
        return match;
      }
      const currentIndex = sourceSectionIndex++;
      return `<div class="sources-section" data-source-index="${currentIndex}"></div>\n`;
    }
  );

  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          // Custom component renderers for better styling
          h1: ({ children }) => <h1 className="markdown-h1">{children}</h1>,
          h2: ({ children }) => <h2 className="markdown-h2">{children}</h2>,
          h3: ({ children }) => <h3 className="markdown-h3">{children}</h3>,
          h4: ({ children }) => <h4 className="markdown-h4">{children}</h4>,
          h5: ({ children }) => <h5 className="markdown-h5">{children}</h5>,
          h6: ({ children }) => <h6 className="markdown-h6">{children}</h6>,
          p: ({ children, node }: any) => {
            // Don't render <p> tags inside list items - just return the children
            if (node?.position?.start?.line && node.position.start.line > 0) {
              // Check if parent is a list item by examining the structure
              const parent = node?.parent;
              if (parent?.type === 'listItem') {
                return <>{children}</>;
              }
            }
            return <p className="markdown-p">{children}</p>;
          },
          div: ({ className, children, ...props }: any) => {
            if (className === 'modify-artifact-section') {
              // Hide Modify Artifact section when artifact limit is reached
              if (hideModifyArtifact) {
                return null;
              }
              return (
                <ModifyArtifactWrapper
                  checkedItems={checkedModifyItems}
                  toggleItem={toggleModifyItem}
                  onSubmit={handleUpdateChanges}
                >
                  {children}
                </ModifyArtifactWrapper>
              );
            }
            if (className === 'suggested-next-steps-section') {
              return (
                <SuggestedNextStepsContext.Provider value={true}>
                  <SuggestedStepClickContext.Provider value={onSuggestedStepClick}>
                    <div className="suggested-next-steps-wrapper">{children}</div>
                  </SuggestedStepClickContext.Provider>
                </SuggestedNextStepsContext.Provider>
              );
            }
            if (className === 'sources-section') {
              const sourceIndex = props['data-source-index'];
              const sources = sourcesData.get(parseInt(sourceIndex, 10)) || [];
              return <SourcesWrapper sourcesData={sources}>{children}</SourcesWrapper>;
            }
            return <div className={className}>{children}</div>;
          },
          ul: ({ children, className }: any) => {
            return <ul className={`markdown-ul ${className || ''}`}>{children}</ul>;
          },
          ol: ({ children, className }: any) => {
            return <ol className={`markdown-ol ${className || ''}`}>{children}</ol>;
          },
          li: ({ children }) => <ListItem>{children}</ListItem>,
          blockquote: ({ children }) => <blockquote className="markdown-blockquote">{children}</blockquote>,
          code: ({ className, children, ...props }: any) => {
            const match = /language-(\w+)/.exec(className || '');
            const isInline = !className || !match;
            const language = match ? match[1] : '';
            const isSvgCode = language === 'svg' || language === 'xml';

            if (isInline) {
              return (
                <code className={`markdown-inline-code ${className}`} {...props}>
                  {children}
                </code>
              );
            }

            // For SVG/XML code, use syntax highlighting
            if (isSvgCode) {
              return (
                <div className={`markdown-code-block svg-code-block ${isSvgCodeCollapsed ? 'collapsed' : ''}`}>
                  <div className="markdown-code-lang">
                    {language}
                    <button
                      className="svg-code-toggle"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        toggleSvgCodeCollapsed();
                      }}
                      aria-label={isSvgCodeCollapsed ? 'Expand code' : 'Collapse code'}
                      type="button"
                    >
                      {isSvgCodeCollapsed ? <ChevronRight /> : <ChevronDown />}
                    </button>
                  </div>
                  {!isSvgCodeCollapsed && (
                    <div
                      ref={isStreaming ? svgCodeBlockRef : null}
                      className="syntax-highlighter-wrapper"
                    >
                      <SyntaxHighlighter
                        language="xml"
                        style={vscDarkPlus}
                        customStyle={{
                          margin: 0,
                          borderRadius: 0,
                          background: '#2d3748',
                        }}
                        PreTag="div"
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    </div>
                  )}
                </div>
              );
            }

            // For other code blocks, use default rendering
            return (
              <div className="markdown-code-block">
                <div className="markdown-code-lang">{language}</div>
                <pre className={`markdown-pre ${className}`}>
                  <code className="markdown-code" {...props}>
                    {children}
                  </code>
                </pre>
              </div>
            );
          },
          pre: ({ children }) => <>{children}</>,
          table: ({ children }) => (
            <div className="markdown-table-wrapper">
              <table className="markdown-table">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="markdown-thead">{children}</thead>,
          tbody: ({ children }) => <tbody className="markdown-tbody">{children}</tbody>,
          tr: ({ children }) => <tr className="markdown-tr">{children}</tr>,
          th: ({ children }) => <th className="markdown-th">{children}</th>,
          td: ({ children }) => <td className="markdown-td">{children}</td>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="markdown-link">
              {children}
            </a>
          ),
          hr: () => <hr className="markdown-hr" />,
          strong: ({ children }) => <strong className="markdown-strong">{children}</strong>,
          em: ({ children }) => <em className="markdown-em">{children}</em>,
          del: ({ children }) => <del className="markdown-del">{children}</del>,
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;